
// src/pages/AddSubcategoryModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createSubcategory } from "../../lib/servicesApi";

const AddSubcategoryModal = ({
    show,
    onClose,
    onCreated,
    categories = [],
    initialCategoryKey = "",
}) => {
    const [form, setForm] = useState({
        categoryKey: initialCategoryKey || "",
        key: "",
        label: "",
        active: true,
    });
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const activeCats = useMemo(() => categories.filter((c) => c.active), [categories]);

    useEffect(() => {
        if (show) {
            setForm({
                categoryKey: initialCategoryKey || activeCats[0]?.key || "",
                key: "",
                label: "",
                active: true,
            });
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, initialCategoryKey, activeCats.length]);

    if (!show) return null;

    const onChange = (e) => {
        const { name, type, value, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const { categoryKey, key, label } = form;
        if (!categoryKey || !key) {
            setError(new Error("Category and subcategory key are required."));
            return;
        }

        try {
            setSaving(true);
            const ref = await createSubcategory({
                categoryKey,
                key: key.trim(),
                label: label?.trim() || key.trim(),
                active: !!form.active,
            });
            onCreated?.(key.trim(), categoryKey, ref?.id);
            onClose?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal d-block" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content shadow">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Subcategory</h5>
                        <button className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <form onSubmit={onSubmit}>
                        <div className="modal-body">
                            {error && <div className="alert alert-danger">{error.message}</div>}

                            <div className="mb-3">
                                <label className="form-label" htmlFor="subcat-category">
                                    Parent Category
                                </label>
                                <select
                                    id="subcat-category"
                                    name="categoryKey"
                                    className="form-select"
                                    value={form.categoryKey}
                                    onChange={onChange}
                                    required
                                >
                                    {activeCats.length === 0 ? (
                                        <option value="">— No active categories —</option>
                                    ) : (
                                        activeCats.map((c) => (
                                            <option key={c.id} value={c.key}>
                                                {c.label}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label" htmlFor="subcat-key">
                                    Subcategory Key
                                </label>
                                <input
                                    id="subcat-key"
                                    name="key"
                                    className="form-control"
                                    placeholder="e.g., deep-clean"
                                    value={form.key}
                                    onChange={onChange}
                                    required
                                />
                                <small className="text-muted">
                                    Unique identifier (used to reference this subcategory).
                                </small>
                            </div>

                            <div className="mb-3">
                                <label className="form-label" htmlFor="subcat-label">
                                    Label (optional)
                                </label>
                                <input
                                    id="subcat-label"
                                    name="label"
                                    className="form-control"
                                    placeholder="e.g., Deep Cleaning"
                                    value={form.label}
                                    onChange={onChange}
                                />
                            </div>

                            <div className="form-check">
                                <input
                                    id="subcat-active"
                                    name="active"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={form.active}
                                    onChange={onChange}
                                />
                                <label className="form-check-label" htmlFor="subcat-active">
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-outline-secondary" type="button" onClick={onClose}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" type="submit" disabled={saving || !form.categoryKey}>
                                {saving ? "Saving..." : "Add Subcategory"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {/* simple backdrop */}
            {/* <div className="modal-backdrop fade show" /> */}
        </div>
    );
};

export default AddSubcategoryModal;
